import { Button } from 'antd';
import { Download, FileX, ShieldAlert } from 'lucide-react';
import { useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router';
import { DetailPageSkeleton } from '../../../../../shared/components/DetailPageSkeleton';
import { EmptyState } from '../../../../../shared/components/EmptyState';
import { PageHeader } from '../../../../../shared/components/PageHeader';
import { withinSection } from '../../../../../shared/components/section-trail';
import { useToast } from '../../../../../shared/hooks/use-toast';
import { rateCardName } from '../../../components/build-rate-card-columns';
import { useRateCardPermissions } from '../../../hooks/use-rate-card-permissions';
import { useRateCardRecord } from '../../../hooks/use-rate-card-record';
import { rateLineExportUrl, rateLineTemplateUrl } from '../../../rate-cards.api';
import { rateCardPath, rateCardTabPath } from '../../../rate-cards.paths';
import { RateLineImportBody } from '../components/RateLineImportBody';
import { RateLineImportPriceChangeNote } from '../components/RateLineImportPriceChangeNote';
import { useRateLineImport } from '../hooks/use-rate-line-import';

/** A spreadsheet round trip for one card's lines: export or start from the template, pick a file, see the diff, then commit — which opens the same draft proposals a hand-typed price change would. */
export function ImportRateLinesPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { state: cardState } = useRateCardRecord(id);
  const { canEdit } = useRateCardPermissions();
  const { state, preview, commit, reset } = useRateLineImport(id);
  const [reason, setReason] = useState('');
  const toast = useToast();

  if (cardState.status === 'forbidden' || !canEdit) return <Navigate to={rateCardTabPath(id, 'lines')} replace />;
  if (cardState.status === 'loading') return <DetailPageSkeleton label="Loading rate card" breadcrumbs={withinSection('rate-cards')} fields={2} />;
  if (cardState.status === 'not_found')
    return <EmptyState icon={FileX} title="Rate card not found" description="This rate card doesn't exist, or the link is out of date." />;
  if (cardState.status === 'error') {
    return (
      <EmptyState icon={ShieldAlert} title="Couldn't load this rate card" description="Something went wrong loading this rate card. Try reloading the page." />
    );
  }

  const { card } = cardState;

  async function handleCommit(): Promise<void> {
    const result = await commit(reason);
    if (result?.ok) {
      toast.success(`Import committed — ${result.createdCount} draft proposal${result.createdCount === 1 ? '' : 's'} opened for approval`);
    }
  }

  return (
    <>
      <PageHeader
        breadcrumbs={withinSection(
          'rate-cards',
          { label: rateCardName(card), to: rateCardPath(card.id) },
          { label: 'Lines', to: rateCardTabPath(card.id, 'lines') },
        )}
        title="Import lines"
        description="Bulk-edit this card's prices in a spreadsheet, then bring the changes back as draft proposals."
        actions={
          <>
            <a href={rateLineTemplateUrl()}>
              <Button icon={<Download className="size-4" />}>Blank template</Button>
            </a>
            <a href={rateLineExportUrl(card.id, false)}>
              <Button icon={<Download className="size-4" />}>Export current lines</Button>
            </a>
          </>
        }
      />
      <RateLineImportPriceChangeNote />
      <RateLineImportBody
        state={state}
        reason={reason}
        onReasonChange={setReason}
        onChoose={preview}
        onCommit={handleCommit}
        onReset={reset}
        onGoToProposals={() => navigate(rateCardTabPath(card.id, 'proposals'))}
      />
    </>
  );
}
